import { useMemo, useState } from "react";

import { Icon } from "@blueprintjs/core";

import { enableReviews } from "@/components/helpers/configuration/utils";
import { Box, Flex } from "@/components/layout/flexbox";
import { Media } from "@/components/layout/Media";
import { ServiceDetails } from "@/components/pieces/coaching/ServiceDetails";
import { LinkButton } from "@/components/pieces/link/LinkButton";
import { CARD_CLASSES, SUBTEXT_COLOR } from "@/css/constants";
import { FullReview } from "@/types/meetings";
import { CoachingUser, RoleView } from "@/types/users";
import { ensureNonNullArray } from "@/utils/array";

import { ReviewBasics } from "./ReviewBasics";

interface OngoingReviewsInnerProps {
  ongoingReviews: FullReview[];
  reviewsLink: string;
  customerId?: string;
  view?: RoleView;
  selectedReviewId?: string;
  onSetSelectedReviewId: (reviewId?: string) => void;
}

function OngoingReviewsInner({
  ongoingReviews,
  reviewsLink,
  customerId,
  view = "coach",
  selectedReviewId,
  onSetSelectedReviewId,
}: OngoingReviewsInnerProps) {
  if (!ongoingReviews.length) return null;

  return (
    <>
      <Flex pb={3} alignItems="center" justifyContent="space-between">
        <Box fontSize={20} fontWeight={600}>
          Ongoing Reviews
        </Box>
        <Box>
          <LinkButton to={reviewsLink}>
            <Media at="xs">
              <Icon icon="history" color={SUBTEXT_COLOR} size={20} />
            </Media>
            <Media greaterThan="xs">History</Media>
          </LinkButton>
        </Box>
      </Flex>
      {ongoingReviews.map((review, index) => (
        <Box
          key={review.id}
          className="round-corners bas"
          mt={index > 0 ? 22 : 0}
          px={24}
          py={3}
        >
          <ReviewBasics
            review={review}
            showMessageButton={true}
            chatChannelId={review.chatChannelId}
            showMessageCount={true}
            view={view}
            customerId={customerId}
          />
          {selectedReviewId === review.id ? (
            <ServiceDetails
              customerId={customerId}
              review={review}
              onClose={() => onSetSelectedReviewId()}
            />
          ) : null}
        </Box>
      ))}
    </>
  );
}

interface OngoingReviewsProps {
  user: CoachingUser;
  customerId?: string;
  reviewsLink?: string;
  mt?: number;
  view?: RoleView;
}

export function OngoingReviews({
  user,
  customerId,
  reviewsLink = "/reviews",
  mt = 3,
  view = "coach",
}: OngoingReviewsProps) {
  const [selectedReviewId, setSelectedReviewId] = useState<string>();
  const [defaultUrl] = useState(document.location.href);

  function onSetSelectedReviewId(reviewId?: string) {
    setSelectedReviewId(reviewId);
    const newUrl = reviewId ? `/reviews/${reviewId}` : defaultUrl;
    if (document.location.href !== newUrl) {
      window.history.replaceState(null, "", newUrl);
    }
  }

  const ongoingReviews = ensureNonNullArray(user.ongoingReviews);
  const hasOngoing = ongoingReviews.length;

  const disableReviews = useMemo(() => {
    return !enableReviews(user);
  }, [user]);

  if (disableReviews || !hasOngoing) return null;

  return (
    <>
      <Box className={CARD_CLASSES} backgroundColor="#FFF" mt={mt} p={24}>
        <OngoingReviewsInner
          ongoingReviews={ongoingReviews}
          reviewsLink={reviewsLink}
          selectedReviewId={selectedReviewId}
          onSetSelectedReviewId={onSetSelectedReviewId}
          customerId={customerId}
          view={view}
        />
      </Box>
    </>
  );
}
